import React, { RefObject, useCallback } from 'react';

import { CommonActions, useNavigation } from '@react-navigation/native';

import { ReuseIcon } from '@/assets/icons/reuse';
import { ShareEntryIcon } from '@/assets/icons/shareEntry';
import { ViewEntryIcon } from '@/assets/icons/viewEntry';
import { EntryActionButton } from '@/components/checkout/EntryActionButton';
import { usePicksNotAddedModal } from '@/feature/betslip-sbk/components/picks-not-added-modal/PicksNotAddedModalProvider';
import { getActiveSbkBetSlipStore, useSbkBetSlipStore } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import { getSubmittedNotAddedSelections } from '@/feature/betslip-sbk/utils/betslip-utils';
import { useIsPredictionsProduct } from '@/hooks/use-is-predictions-product';
import { runStoreUpdate } from '@/utils/zustand';

import { BetSuccessNavigationProp } from '../../types';

type Props = {
    hasCombo: boolean;
    betIds: string[];
    isMultipleEntryType: boolean;
    pushPrimerEligibility: boolean;
    keepBetDataWhenScreenRemoved: RefObject<boolean>;
    onDone: () => void;
};

export const BetSuccessButtons = ({
    hasCombo,
    betIds,
    isMultipleEntryType,
    pushPrimerEligibility,
    keepBetDataWhenScreenRemoved,
    onDone,
}: Props) => {
    const navigation = useNavigation<BetSuccessNavigationProp>();
    const { showPicksNotAddedModal } = usePicksNotAddedModal();
    const isPrediction = useIsPredictionsProduct();
    const homeRoute = isPrediction ? 'PredictionHome' : 'SweepsHome';
    const entriesTabRoute = isPrediction ? 'PredictionEntries' : 'SweepsEntries';

    const clearBetSlip = useSbkBetSlipStore(state => state.actions.clearBetSlip);
    const keepUnstakedSelections = useSbkBetSlipStore(state => state.actions.keepUnstakedSelections);
    const keepSelections = useSbkBetSlipStore(state => state.actions.keepSelections);

    const clearOrKeepUnstaked = useCallback(() => {
        if (hasCombo) {
            runStoreUpdate(clearBetSlip);
        } else {
            runStoreUpdate(keepUnstakedSelections);
        }
    }, [hasCombo, clearBetSlip, keepUnstakedSelections]);

    const navigateToPushPrimer = useCallback(() => {
        setTimeout(() => {
            navigation.navigate('PushPrimer');
        }, 500);
    }, [navigation]);

    const handleShare = useCallback(() => {
        const betId = betIds[0];
        if (betId) {
            navigation.navigate('BetShareScreen', { betId });
        }
    }, [betIds, navigation]);

    const handleReusePicks = useCallback(() => {
        const betSlipStore = getActiveSbkBetSlipStore();
        const { closedSelections, actions } = betSlipStore.getState();

        // Remove closed selections first so they appear in the "not added" diff
        if (closedSelections.length > 0) {
            runStoreUpdate(actions.removeClosedSelections);
        }

        const { selections, submittedState } = betSlipStore.getState();
        const notAddedSelections = getSubmittedNotAddedSelections(selections, submittedState);
        const selectionCount = Object.keys(selections).length;

        if (notAddedSelections.length > 0) {
            showPicksNotAddedModal(notAddedSelections);
        }

        if (selectionCount > 0) {
            keepBetDataWhenScreenRemoved.current = true;
            runStoreUpdate(keepSelections);
            navigation.goBack();
        } else {
            onDone();
        }
    }, [showPicksNotAddedModal, keepSelections, navigation, onDone, keepBetDataWhenScreenRemoved]);

    const handleViewEntry = useCallback(() => {
        keepBetDataWhenScreenRemoved.current = true;
        clearOrKeepUnstaked();

        const betId = betIds[0];
        // Navigate to specific bet screen for single/parlay entries
        if (!isMultipleEntryType && betId) {
            navigation.dispatch(
                CommonActions.reset({
                    index: 1,
                    routes: [{ name: homeRoute }, { name: 'SbkBetScreen', params: { betId } }],
                })
            );
        } else {
            // For multiple entries (or missing betId), go to the entries list.
            // Reset the root stack to the product home with the Entries tab selected
            // in a single atomic dispatch — this dismisses the betslip stack and
            // switches tab at once, avoiding the race between a parent goBack and a
            // navigate.
            navigation.dispatch(
                CommonActions.reset({
                    index: 0,
                    routes: [
                        {
                            name: homeRoute,
                            state: { routes: [{ name: entriesTabRoute }] },
                        },
                    ],
                })
            );
        }

        if (pushPrimerEligibility) {
            navigateToPushPrimer();
        }
    }, [
        keepBetDataWhenScreenRemoved,
        clearOrKeepUnstaked,
        betIds,
        isMultipleEntryType,
        pushPrimerEligibility,
        navigation,
        navigateToPushPrimer,
        homeRoute,
        entriesTabRoute,
    ]);

    return (
        <>
            {!isMultipleEntryType ? (
                <EntryActionButton
                    icon={<ShareEntryIcon />}
                    label="Share"
                    testID="entryConfirmationShareEntryButton"
                    onPress={handleShare}
                />
            ) : null}
            <EntryActionButton
                icon={<ReuseIcon />}
                label="Reuse"
                testID="entryConfirmationReuseLineupButton"
                onPress={handleReusePicks}
            />
            <EntryActionButton
                icon={<ViewEntryIcon />}
                label="View"
                testID="entryConfirmationViewEntryButton"
                onPress={handleViewEntry}
            />
        </>
    );
};
